import React from 'react';
import PropTypes from 'prop-types';
import {useNavigate} from 'react-router-dom';

import {dispatch, connect} from 'utils/state_manager';

import {Box, Stack, Typography} from 'views/components';
import {Translate, translate} from 'views/module';

import ActionLog from './ActionLog';
import ActionLogTextbox from './ActionLogTextbox';
import AgentJobsDraft from './agent_jobs/draft/AgentJobsDraft';
import AgentJobsList from './agent_jobs/AgentJobsList';
import CaseDomainGuide from './CaseDomainGuide';
import CaseMarks from './marking/CaseMarks';
import CaseTitle from './CaseTitle';

import backend from 'utils/backend';
import featureFlags from 'utils/feature_flags/feature_flags';

const noop = () => undefined;

const serializePayload = (payload) => {
  try {
    return JSON.stringify(payload || {});
  } catch {
    return '';
  }
};

const CaseView = (props) => {
  const {
    caseRn,
    isEmbedded = false,
    onCaseDataChange = noop,
    participantDetails = {},
    statusProgressWrap: progressWrap,
    userRn,
  } = props;
  const navigate = useNavigate();
  const [caseData, setCaseData] = React.useState(null);
  const [caseDataLoaded, setCaseDataLoaded] = React.useState(false);
  const [agentJobs, setAgentJobs] = React.useState([]);
  const [agentJobsLoaded, setAgentJobsLoaded] = React.useState(false);
  const [agentJobsLoadFailed, setAgentJobsLoadFailed] = React.useState(false);
  const [resolvedDomainGuide, setResolvedDomainGuide] = React.useState(null);
  const [resolvedDomainGuideLoaded, setResolvedDomainGuideLoaded] = React.useState(false);
  const [resolvedDomainGuideLoadFailed, setResolvedDomainGuideLoadFailed] = React.useState(false);

  const domainGuideRn = caseData?.domainGuideRn || null;

  React.useEffect(() => {
    let isActive = true;

    setCaseData(null);
    setCaseDataLoaded(false);
    onCaseDataChange(null);

    const loadCase = async () => {
      await progressWrap(async () => {
        try {
          const caseResponse = await backend.case.getCase(caseRn);

          if (isActive) {
            const nextCaseData = caseResponse || null;
            setCaseData(nextCaseData);
            setCaseDataLoaded(true);
            onCaseDataChange(nextCaseData);
          }
        } catch {
          if (isActive) {
            setCaseData(null);
            setCaseDataLoaded(true);
            onCaseDataChange(null);
          }
        }
      });
    };

    loadCase();

    return () => {
      isActive = false;
    };
  }, [caseRn, onCaseDataChange, progressWrap]);

  React.useEffect(() => {
    let isActive = true;

    setAgentJobs([]);
    setAgentJobsLoaded(false);
    setAgentJobsLoadFailed(false);

    const loadAgentJobs = async () => {
      try {
        const agentJobsResponse = await backend.agentJobs.getCaseJobs(caseRn);

        if (isActive) {
          setAgentJobs(agentJobsResponse?.jobs || []);
          setAgentJobsLoaded(true);
        }
      } catch {
        if (isActive) {
          setAgentJobs([]);
          setAgentJobsLoaded(true);
          setAgentJobsLoadFailed(true);
        }
      }
    };

    loadAgentJobs();

    return () => {
      isActive = false;
    };
  }, [caseRn]);

  const pendingAgentJobs = React.useMemo(() => {
    return agentJobs.filter((job) => job.status === 'PENDING');
  }, [agentJobs]);
  const previousAgentJobs = React.useMemo(() => {
    return agentJobs.filter((job) => job.status !== 'PENDING');
  }, [agentJobs]);

  React.useEffect(() => {
    if (pendingAgentJobs.length === 0) {
      return undefined;
    }

    let isActive = true;
    const interval = window.setInterval(async () => {
      try {
        const agentJobsResponse = await backend.agentJobs.getCaseJobs(caseRn);

        if (isActive) {
          setAgentJobs(agentJobsResponse?.jobs || []);
        }
      } catch {
        // Keep the last known jobs on a transient poll failure.
      }
    }, 15000);

    return () => {
      isActive = false;
      window.clearInterval(interval);
    };
  }, [caseRn, pendingAgentJobs.length]);

  React.useEffect(() => {
    let isActive = true;

    setResolvedDomainGuide(null);
    setResolvedDomainGuideLoaded(false);
    setResolvedDomainGuideLoadFailed(false);

    if (!domainGuideRn) {
      return undefined;
    }

    const loadDomainGuide = async () => {
      try {
        const domainGuideResponse = await backend.domainGuide.getDomainGuide(domainGuideRn);
        const domainGuide = domainGuideResponse?.domainGuide || domainGuideResponse || null;
        const rawDomainGuide = typeof domainGuide?.rawDomainGuide === 'string' ?
          domainGuide.rawDomainGuide :
          null;

        if (isActive) {
          setResolvedDomainGuide(rawDomainGuide);
          setResolvedDomainGuideLoaded(true);
          setResolvedDomainGuideLoadFailed(rawDomainGuide === null);
        }
      } catch {
        if (isActive) {
          setResolvedDomainGuide(null);
          setResolvedDomainGuideLoaded(true);
          setResolvedDomainGuideLoadFailed(true);
        }
      }
    };

    loadDomainGuide();

    return () => {
      isActive = false;
    };
  }, [caseRn, domainGuideRn]);

  if (!caseDataLoaded) {
    return null;
  }

  if (!caseData) {
    return (
      <Box
        sx={{
          textAlign: 'left',
          px: {
            xs: 2,
            md: 2,
            lg: 3,
          },
          py: 3,
          width: isEmbedded ? '100%' : {
            xs: '100%',
            md: '75%',
          },
        }}
      >
        <Typography
          sx={{
            fontSize: '18px',
            fontWeight: 500,
            color: '#050A14',
          }}
        >
          <Translate expression='case.Detail.NotFound' />
        </Typography>
      </Box>
    );
  }

  const updateStatus = (status) => {
    if (!caseData.caseRn || status === caseData.status) {
      return;
    }

    progressWrap(async () => {
      const statusResponse = await backend.case.updateStatus(caseData.caseRn, status);
      const nextCaseData = {
        ...caseData,
        status: statusResponse.status,
      };

      setCaseData(nextCaseData);
      onCaseDataChange(nextCaseData);
    });
  };

  const updatePriority = (priority) => {
    if (!caseData.caseRn || priority === Number(caseData.priority)) {
      return;
    }

    progressWrap(async () => {
      const priorityResponse = await backend.case.updatePriority(caseData.caseRn, priority);
      const nextCaseData = {
        ...caseData,
        priority: priorityResponse.priority,
      };

      setCaseData(nextCaseData);
      onCaseDataChange(nextCaseData);
    });
  };

  const createActionLog = async (actionLogMessage) => {
    const eventRouterCommandRegex = /(^|\s)@hudson\b\s*/i;
    const eventRouterMessage = eventRouterCommandRegex.test(actionLogMessage) ?
      actionLogMessage.replace(eventRouterCommandRegex, '$1').trim() :
      null;

    if (eventRouterMessage !== null) {
      const params = new URLSearchParams({
        caseRn: caseData.caseRn,
        target: 'eventRouterDiscipline',
      });

      if (eventRouterMessage) {
        params.set('message', eventRouterMessage);
      }

      navigate(`/agent/chat?${params.toString()}`);
      return true;
    }

    let actionLogCreated = false;
    const message = translate('case.Detail.ActionLog.AdminMessage', {userRn, actionLogMessage});

    await progressWrap(async () => {
      const actionLogResponse = await backend.case.createActionLog(caseData.caseRn, message);
      const nextCaseData = {
        ...caseData,
        actionLog: [
          ...(caseData.actionLog || []),
          {
            message: actionLogResponse.message,
            createdAt: new Date().toISOString(),
          },
        ],
      };

      setCaseData(nextCaseData);
      onCaseDataChange(nextCaseData);
      actionLogCreated = true;
    }, {
      showProgress: false,
    });

    return actionLogCreated;
  };

  const upsertAgentJob = (updatedJob) => {
    if (!updatedJob?.agentJobRn) {
      return;
    }

    setAgentJobs((currentAgentJobs) => {
      const existingIndex = currentAgentJobs.findIndex((job) => job.agentJobRn === updatedJob.agentJobRn);

      if (existingIndex === -1) {
        return [
          ...currentAgentJobs,
          updatedJob,
        ];
      }

      return currentAgentJobs.map((job) => (
        job.agentJobRn === updatedJob.agentJobRn ?
          updatedJob :
          job
      ));
    });
  };

  const saveAgentJob = async (job, actionPayload, executeAt) => {
    let nextJob = job;
    const payloadChanged = serializePayload(actionPayload) !== serializePayload(job.actionPayload);
    const executeAtChanged = executeAt && executeAt !== job.executeAt;

    if (!payloadChanged && !executeAtChanged) {
      return nextJob;
    }

    if (payloadChanged) {
      const payloadResponse = await backend.agentJobs.updatePayload(job.agentJobRn, actionPayload);
      nextJob = payloadResponse?.job || nextJob;
      upsertAgentJob(nextJob);
    }

    if (executeAtChanged) {
      const executionTimeResponse = await backend.agentJobs.updateExecutionTime(job.agentJobRn, executeAt);
      nextJob = executionTimeResponse?.job || {...nextJob, executeAt};
      upsertAgentJob(nextJob);
    }

    return nextJob;
  };

  const cancelAgentJob = async (job) => {
    const cancelResponse = await backend.agentJobs.cancelJob(job.agentJobRn);
    const nextJob = cancelResponse?.job || {
      ...job,
      status: 'CANCELLED',
    };

    upsertAgentJob(nextJob);
    return nextJob;
  };

  const sendAgentJobNow = async (job, executeAt) => {
    const executionTimeResponse = await backend.agentJobs.updateExecutionTime(job.agentJobRn, executeAt);
    const nextJob = executionTimeResponse?.job || {
      ...job,
      executeAt,
    };

    upsertAgentJob(nextJob);
    return nextJob;
  };

  return (
    <Box
      sx={{
        textAlign: 'left',
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        minHeight: 0,
        width: isEmbedded ? '100%' : {
          xs: '100%',
          md: '75%',
        },
      }}
    >
      <Stack
        sx={{
          'gap': 2.5,
          'flex': 1,
          'minHeight': 0,
          'overflowY': 'auto',
          'px': {
            xs: 2,
            md: 4.5,
            lg: 4.5,
          },
          'pt': 3.5,
          'pb': 2,
          'scrollbarColor': '#CFCFD4 transparent',
          '&::-webkit-scrollbar': {
            width: '8px',
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: '#CFCFD4',
            borderRadius: '999px',
          },
          '& > *': {
            flexShrink: 0,
          },
        }}
      >
        <CaseTitle
          caseData={caseData}
          participantDetails={participantDetails}
          updatePriority={updatePriority}
          updateStatus={updateStatus}
        />
        <CaseDomainGuide
          domainGuide={domainGuideRn ? resolvedDomainGuide : caseData.domainGuide}
          isLoading={Boolean(domainGuideRn) && !resolvedDomainGuideLoaded}
          loadFailed={resolvedDomainGuideLoadFailed}
        />
        <AgentJobsDraft
          agentJobs={pendingAgentJobs}
          loading={!agentJobsLoaded}
          loadFailed={agentJobsLoadFailed}
          onCancelJob={cancelAgentJob}
          onSaveJob={saveAgentJob}
          onSendJobNow={sendAgentJobNow}
          participantDetails={participantDetails}
        />
        <AgentJobsList
          agentJobs={previousAgentJobs}
          participantDetails={participantDetails}
        />
        <ActionLog actionLog={caseData.actionLog || []} />
        {
          (featureFlags.oversightMarkingReadsEnabled || featureFlags.oversightCaseReviewEnabled) &&
          <CaseMarks caseData={caseData} />
        }
      </Stack>
      <Box
        sx={{
          flexShrink: 0,
          borderTop: '1px solid #ECECF1',
          backgroundColor: '#FFFFFF',
          px: {
            xs: 2,
            md: 4.5,
            lg: 4.5,
          },
          py: 1.5,
        }}
      >
        <ActionLogTextbox onSubmit={createActionLog} />
      </Box>
    </Box>
  );
};

CaseView.propTypes = {
  caseRn: PropTypes.string.isRequired,
  isEmbedded: PropTypes.bool,
  onCaseDataChange: PropTypes.func,
  participantDetails: PropTypes.object,
  statusProgressWrap: PropTypes.func.isRequired,
  userRn: PropTypes.string.isRequired,
};

const mapStateToProps = (state) => ({
  userRn: state.user.userRn,
});

export default connect(mapStateToProps, dispatch)(React.memo(CaseView));
